import { useCallback } from 'react';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import type { TemplateConfigInput } from '@server/trading-templates/schemas/input.schema';
import { useTemplateMutations } from './template-mutations.hook';
import { templateNotifications } from './template-notifications';

type SaveTemplateInput = {
  templateId?: string;
  name: string;
  config: TemplateConfigInput;
  isBundle?: boolean;
  label?: string;
  invalidateDefaultTemplates?: boolean;
};

type SaveTemplateResult = {
  templateId: string;
  config: TemplateConfigInput;
  mode: 'create' | 'update';
};

export const useTemplateSave = () => {
  const { notify } = useNotifications();
  const { createTemplate, updateTemplate, isPending } = useTemplateMutations();

  const saveTemplate = useCallback(
    async ({
      templateId,
      name,
      config,
      isBundle = false,
      label = 'Template',
      invalidateDefaultTemplates = false
    }: SaveTemplateInput): Promise<SaveTemplateResult> => {
      try {
        const shouldInvalidateDefaultTemplates =
          invalidateDefaultTemplates || Boolean(templateId);

        if (!templateId) {
          const createdTemplateId = await createTemplate(
            {
              name,
              isBundle,
              config
            },
            {
              invalidateTradingTemplates: true,
              invalidateDefaultTemplates: shouldInvalidateDefaultTemplates
            }
          );

          notify({
            type: 'success',
            message: templateNotifications.created(label)
          });

          return {
            templateId: createdTemplateId,
            config,
            mode: 'create'
          };
        }

        await updateTemplate(
          {
            templateId,
            name,
            config
          },
          {
            invalidateTradingTemplates: true,
            invalidateDefaultTemplates: shouldInvalidateDefaultTemplates
          }
        );

        notify({
          type: 'success',
          message: templateNotifications.updated(label)
        });

        return {
          templateId,
          config,
          mode: 'update'
        };
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : templateId
                ? templateNotifications.updateFailed(label)
                : templateNotifications.createFailed(label)
        });

        throw error;
      }
    },
    [createTemplate, notify, updateTemplate]
  );

  return {
    saveTemplate,
    isPending
  };
};
